import { type FC, type ReactNode } from "react";
import { FullCsticId, type LinearCsticValue, type LinearSubstep, SimpleCsticId } from "../../../models";
import { useSelector } from "react-redux";
import SubstepFrame from "../../SubstepFrame";
import { Col, Row } from "../../../../../common/components/Layout";
import { selectRailAmount, selectSubKmatCstics } from "../../../redux/selectors";
import { CSTIC_PART_SIZING_TYPE_MANUAL, GET_GUIDEWAY_SET_IMAGE_URL } from "../../../constants";
import LinearInfo from "../../LinearInfo";
import { type RootState } from "../../../../../redux/store";
import { PieceLengthInput } from "./components/PieceLengthInput";
import classNames from "classnames";
import LanguageAwareExternalContent from "../../LanguageAwareExternalContent";
import { Card } from "../../Card";
import { makeMinMaxLabel } from "../../../hooks/useALARLogic";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { KmatType } from "~/Configurators/Linear/kmatType";
import { type AlarHelperTextFactory, ExtraConfig, HelperCstics, InputCstics } from "./constants";
import { Stack, styled } from "@mui/material";
import type { ResolvedCsticDefinition } from "~/Configurators/common/v2/StepDefinition";
import { KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import { SimpleCsticNumberField } from "~/Configurators/Shaft/components/SimpleCsticNumberField";
import { SimpleCsticSelectField } from "~/Configurators/Shaft/components/SimpleCsticSelectField";
import { NumberFormatter as NF } from "~/utils/NumberFormatter";
import { getSubKmatCstics } from "~/Configurators/common/v2/helper";
import { LCTileImage } from "../../LinearTile";
import {
  RailLocatingFaceSegment1,
  RailLocatingFaceSegment2,
} from "~/Configurators/Linear/v2/configuration/kllt/KLLTUnitCstics";

import { selectCsticExpanded } from "~/Configurators/Linear/redux/selectCstic";
import { SubstepType } from "~/Configurators/Linear/dtos";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const KLLTGuidewaySetStep = ({ def, ...props }: Props) => {
  const railAmount = useSelector(selectRailAmount);

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <div className={"w-100 mb-30"}>
        <Row className={"d-flex"}>
          {Array.from({ length: railAmount }, (_, index) => (
            <Col size={4} key={index}>
              <SingleGuidewayTile
                index={index}
                def={def}
                kmatType={KmatType.SubRail}
                railAmount={railAmount}
                railIndex={index}
              />
            </Col>
          ))}
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default KLLTGuidewaySetStep;

interface TileProps {
  index: number;
  kmatType: KmatType;
  def: LinearSubstep;
  railAmount: number;
  railIndex: number;
}

const SingleGuidewayTile = (props: TileProps) => {
  const { def, index, kmatType, railAmount, railIndex } = props;

  const substepType = def.type;
  const cstics = useSelector(selectSubKmatCstics(kmatType, index));

  const formatValue = (val: string | undefined) => {
    return val ? NF.getDefaultFormatter().format(+val) : "N/A";
  };
  const { helper: helperCstics, input: inputCstics } = getSubKmatCstics(def, index);

  const makeCstiId = (name: string) => FullCsticId({ key: name, index, type: kmatType });
  const getHelperName = (name: string) => helperCstics[name]?.name;

  const xmlT = useXmlConfiguratorTranslations().t!;
  const { t } = useTranslation();

  const minCsticName = getHelperName(HelperCstics.totalLengthMin);
  const minLength = cstics[minCsticName]?.value;
  const maxCsticName = getHelperName(HelperCstics.totalLengthMax);
  const maxLength = cstics[maxCsticName]?.value;
  const lengthHelperLabel = `${formatValue(minLength)}mm - ${formatValue(maxLength)}mm`;

  const totalLengthDef = inputCstics[InputCstics.TotalLength];

  const partAmountCsticDef = inputCstics[InputCstics.partAmount];

  const partSizingCsticDef = inputCstics[InputCstics.partSizingType];
  const partSizingCstic = cstics[partSizingCsticDef.name];
  const isCustomPieceSizes = partSizingCstic?.value === CSTIC_PART_SIZING_TYPE_MANUAL;

  const renderDropdownEntry = (value: LinearCsticValue) => <LanguageAwareExternalContent content={value.label} />;

  const alarVisible = inputCstics[InputCstics.aL].visible && inputCstics[InputCstics.aR].visible;

  const makeGetValue = (source: Record<string, ResolvedCsticDefinition>) => (csticName: string) => {
    const stringVal = cstics[source[csticName]?.name]?.value;
    if (stringVal) {
      return +stringVal;
    } else {
      return undefined;
    }
  };
  const getHelperValue = makeGetValue(helperCstics);
  const getValue = makeGetValue(inputCstics);

  const alarHelperData = [
    {
      recommendedMin: getHelperValue(HelperCstics.alRecommendedMin),
      recommendedMax: getHelperValue(HelperCstics.alRecommendedMax),
      hardMin: 0,
      hardMax: getHelperValue(HelperCstics.alHardMax),
      value: getValue(InputCstics.aL) ?? null,
      t,
    },
    {
      recommendedMin: getHelperValue(HelperCstics.arRecommendedMin),
      recommendedMax: getHelperValue(HelperCstics.arRecommendedMax),
      hardMin: 0,
      hardMax: getHelperValue(HelperCstics.arHardMax),
      value: getValue(InputCstics.aR) ?? null,
      t,
    },
  ];

  const helperTextFactory = def.extraConfig[ExtraConfig.AlarHelperTextFactory] as AlarHelperTextFactory;
  const alHelperText = helperTextFactory(alarHelperData[0]);
  const arHelperText = helperTextFactory(alarHelperData[1]);

  let lengthDecimals;
  let alarDecimals;
  const productCode = useSelector((state: RootState) => state.linearConfigurator.productCode);

  if (productCode === KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE) {
    lengthDecimals = 1;
    alarDecimals = 2;
  } else {
    lengthDecimals = 0;
    alarDecimals = 1;
  }

  let showNotRecommendedWarning = (def.extraConfig[ExtraConfig.showNotRecommendedWarning] ?? false) as boolean;
  if (showNotRecommendedWarning) {
    showNotRecommendedWarning = alarHelperData.some((helperData) => {
      if (!helperData.value || !helperData.recommendedMin || !helperData.recommendedMax) {
        return false;
      }
      return helperData.value < helperData.recommendedMin || helperData.value > helperData.recommendedMax;
    });
  }

  // TODO - Move to configuration
  let locatingFaceCsticName;
  if (railIndex === 0) {
    locatingFaceCsticName = RailLocatingFaceSegment1;
  } else {
    locatingFaceCsticName = RailLocatingFaceSegment2;
  }
  const { cstic: locatingFaceCstic } = useSelector(
    selectCsticExpanded(SimpleCsticId(locatingFaceCsticName), SubstepType.LocatingFace),
  );

  return (
    <Card disableHover={true}>
      <StyledStack gap={2}>
        {kmatType === KmatType.SubRail && (
          <>
            <LCTileImage className={"w-100"} imageUrl={GET_GUIDEWAY_SET_IMAGE_URL(railAmount, railIndex + 1)} />
            <LinearSectionTitle className={"my-0"}>
              {t(`page.linear.guidewaySet.title.${locatingFaceCstic?.value == "U" ? "below" : "above"}`, {
                index: index + 1,
              })}
            </LinearSectionTitle>
          </>
        )}

        {def.extraTexts["upperMessage"] && (
          <Alert severity={"info"}>
            <LanguageAwareExternalContent content={def.extraTexts["upperMessage"]} />
          </Alert>
        )}

        <LinearSection label={xmlT(def.extraTexts["lengthName"])} info={def.extraTexts["lengthInfo"]}>
          <SimpleCsticNumberField
            csticId={makeCstiId(totalLengthDef.name)}
            substepType={substepType}
            disabled={totalLengthDef.disabled}
            helperLabel={lengthHelperLabel}
            required={totalLengthDef.required}
            decimalScale={lengthDecimals}
          />
        </LinearSection>

        <Alert severity={"info"} className={"pb-3"}>
          <LanguageAwareExternalContent content={def.extraTexts["lowerMessage"]} />
        </Alert>

        {alarVisible && (
          <>
            <LinearSection
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["alarName"]}
                  loadedClassname={"scha__lc__external-label"}
                />
              }
              info={def.extraTexts["alarInfo"]}
            >
              <Row>
                <Col size={6}>
                  <SimpleCsticNumberField
                    useCsticLabel
                    csticId={makeCstiId(inputCstics[InputCstics.aL].name)}
                    substepType={substepType}
                    disabled={isCustomPieceSizes}
                    required={inputCstics[InputCstics.aL].required}
                    decimalScale={alarDecimals}
                    helperLabel={alHelperText}
                  />
                </Col>

                <Col size={6}>
                  <SimpleCsticNumberField
                    useCsticLabel
                    csticId={makeCstiId(inputCstics[InputCstics.aR].name)}
                    substepType={substepType}
                    disabled={isCustomPieceSizes || inputCstics[InputCstics.aR].disabled}
                    required={inputCstics[InputCstics.aR].required}
                    decimalScale={alarDecimals}
                    helperLabel={arHelperText}
                  />
                </Col>
              </Row>
              {showNotRecommendedWarning && (
                <Alert severity={"info"} className={"mt-3"}>
                  <LanguageAwareExternalContent content={def.extraTexts["alarWarningMessage"]} />
                </Alert>
              )}
            </LinearSection>

            <LinearSection
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["partsSectionName"]}
                  loadedClassname={"scha__lc__external-label"}
                />
              }
              info={def.extraTexts["partsSectionInfo"]}
            >
              <Row>
                <Col size={6}>
                  <SimpleCsticSelectField
                    useCsticLabel
                    csticId={makeCstiId(partSizingCsticDef.name)}
                    disabled={partSizingCsticDef.disabled}
                    substepType={substepType}
                    renderValue={renderDropdownEntry}
                  />
                </Col>

                <Col size={6}>
                  <SimpleCsticSelectField
                    useCsticLabel
                    csticId={makeCstiId(partAmountCsticDef.name)}
                    disabled={partAmountCsticDef.disabled}
                    substepType={substepType}
                    renderValue={renderDropdownEntry}
                  />
                </Col>
              </Row>
              {PIECE_INPUTS.filter((csticName) => inputCstics[csticName]?.visible).map((csticName, partIndex) => (
                <PieceLengthInput
                  key={csticName}
                  cstics={cstics}
                  kmatIndex={index}
                  pieceIndex={partIndex}
                  disabled={inputCstics[csticName].disabled}
                  isSecondLast={false}
                  isLast={partIndex === PIECE_INPUTS.length - 1}
                  substep={substepType}
                  makeMinMaxLabelFormatter={makeMinMaxLabel}
                />
              ))}
            </LinearSection>
          </>
        )}
      </StyledStack>
    </Card>
  );
};

const PIECE_INPUTS = [
  InputCstics.partOne,
  InputCstics.partTwo,
  InputCstics.partThree,
  InputCstics.partFour,
  InputCstics.partFive,
];

const StyledStack = styled(Stack)({
  padding: "16px",
});

const LinearSection: FC<{ label: ReactNode; info?: string; className?: string; children?: ReactNode }> = ({
  children,
  label,
  info,
  className,
}) => {
  return (
    <div className={classNames("mb-30", className)}>
      <LinearSectionTitle className={"mt-0 mb-2"} info={info}>
        {label}
      </LinearSectionTitle>
      {children}
    </div>
  );
};

const LinearSectionTitle: FC<{ info?: string } & JSX.IntrinsicElements["p"]> = ({
  info,
  className,
  children,
  // eslint-disable-next-line unused-imports/no-unused-vars
  ...rest
}) => {
  return (
    <p className={classNames("title-medium d-flex flex-align-center", className)}>
      <span>{children}</span>
      <LinearInfo
        className={"ml-1 flex-align-self-start"}
        popupClassName={"scha__lc__external-content--shaft"}
        info={info}
      />
    </p>
  );
};
